import React from 'react';
import { PieChart, Pie, Cell } from 'recharts';
import { useNavigate } from 'react-router-dom';

// Firebase
import { db } from "../../../../firebaseConfig";

// global
import { useAuth } from '../../../../AuthContext';

// redux
import { useAppSelector } from '../../../../redux/store';

// function fetchCallData
import { fetchCallMetrics } from '../../../../components/CallCRM/Function';
import getCurrentScreen from '../../../../Helper/GetScreen';

// Task service
import {
  fetchTaskMetrics,
  fetchTaskItems,
  TaskItem,
  TaskMetrics,
  TaskFilters
} from '../../../../components/Tasks/TaskService';

// Date formatting
import { format } from 'date-fns';

interface UserOption {
  id: string;
  name: string;
}

interface TotalTaskCountProps {
  users: UserOption[];
  selectedUser: UserOption[];
  startTime: string | null;
  endTime: string | null;
  showTooltip?: boolean;
}
const TotalTaskCount: React.FC<TotalTaskCountProps> = ({ users, selectedUser, startTime, endTime, showTooltip = false }) => {
  const { userData, currentUser, isAdmin, isManager, isStaff } = useAuth();
  const currentScreen = getCurrentScreen(useAppSelector(state => state.screen.currentScreen), isAdmin, isManager, isStaff);
  const navigate = useNavigate();
  // const currentScreen = getCurrentScreen(useAppSelector(state => state.screen.currentScreen), isAdmin, isManager, isStaff);

  // Panel state
  const [isPanelOpen, setIsPanelOpen] = React.useState(false);
  const [selectedTaskType, setSelectedTaskType] = React.useState<string>('');
  const [loading, setLoading] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [panelWidth, setPanelWidth] = React.useState(600); // Default width in pixels (increased from 384px)
  const [isResizing, setIsResizing] = React.useState(false);

  // Pagination state
  const [lastDocument, setLastDocument] = React.useState<any>(null);
  const [hasMore, setHasMore] = React.useState(false);

  // data state - using real data from service
  const [taskMetrics, setTaskMetrics] = React.useState<TaskMetrics>({
    totalTasks: 0,
    dueToday: 0,
    upcoming: 0,
    overdue: 0
  });

  // Real task items data
  const [taskItems, setTaskItems] = React.useState<TaskItem[]>([]);

  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
  const [tooltipContent, setTooltipContent] = React.useState<{ x: number; y: number; content: string } | null>(null);

  // Fetch task metrics
  React.useEffect(() => {
    const fetchMetrics = async () => {
      if (!userData?.companyId || !currentUser?.uid) return;

      try {
        setLoading(true);

        const filters: TaskFilters = {
          companyId: userData.companyId,
          userIds: selectedUser.length > 0 ? selectedUser.map(user => user.id) : undefined,
          startTime,
          endTime,
          currentScreen,
          isAdmin: isAdmin || false,
          isManager: isManager || false,
          isStaff: isStaff || false,
          currentUserId: currentUser.uid
        };

        console.log("Task filters applied:", filters);
        console.log("Selected users for task filtering:", selectedUser);
        console.log("User IDs being sent to TaskService:", filters.userIds);

        const metrics = await fetchTaskMetrics(filters);
        setTaskMetrics(metrics);
      } catch (error) {
        console.error("Error fetching task metrics:", error);
        // Keep default values on error
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, [userData?.companyId, currentUser?.uid, startTime, endTime, selectedUser, currentScreen, isAdmin, isManager, isStaff]);

  const data = [
    { name: 'Due Today', value: taskMetrics?.dueToday || 0 },
    { name: 'Upcoming', value: taskMetrics?.upcoming || 0 },
    { name: 'Overdue', value: taskMetrics?.overdue || 0 },
  ];

  const COLORS = ['#3483be', '#02385e', '#fc583d']; // Due Today (Blue), Upcoming (Dark Blue), Overdue (Red)

  const calculatePercentage = (value: number | undefined) => {
    const total = taskMetrics?.totalTasks || 0;
    if (total === 0) return 0;
    return ((value || 0) / total * 100).toFixed(1);
  };

  const handleTaskClick = async (type: string) => {
    if (!userData?.companyId || !currentUser?.uid) return;

    setSelectedTaskType(type);
    setIsPanelOpen(true);

    // Reset pagination state
    setLastDocument(null);
    setHasMore(false);

    try {
      setLoading(true);

      const filters: TaskFilters = {
        companyId: userData.companyId,
        userIds: selectedUser.length > 0 ? selectedUser.map(user => user.id) : undefined,
        startTime,
        endTime,
        currentScreen,
        isAdmin: isAdmin || false,
        isManager: isManager || false,
        isStaff: isStaff || false,
        currentUserId: currentUser.uid
      };

      const result = await fetchTaskItems(filters, type as 'today' | 'upcoming' | 'overdue', 100);
      setTaskItems(result.items);
      setLastDocument(result.lastDocument);
      setHasMore(result.hasMore);
    } catch (error) {
      console.error(`Error fetching ${type} task items:`, error);
      setTaskItems([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  const handleShowMore = async () => {
    if (!userData?.companyId || !currentUser?.uid || !lastDocument || !hasMore) return;

    try {
      setLoadingMore(true);

      const filters: TaskFilters = {
        companyId: userData.companyId,
        userIds: selectedUser.length > 0 ? selectedUser.map(user => user.id) : undefined,
        startTime,
        endTime,
        currentScreen,
        isAdmin: isAdmin || false,
        isManager: isManager || false,
        isStaff: isStaff || false,
        currentUserId: currentUser.uid
      };

      const result = await fetchTaskItems(
        filters,
        selectedTaskType as 'today' | 'upcoming' | 'overdue',
        100,
        lastDocument
      );

      // Append new items to existing ones
      setTaskItems(prev => [...prev, ...result.items]);
      setLastDocument(result.lastDocument);
      setHasMore(result.hasMore);
    } catch (error) {
      console.error(`Error fetching more ${selectedTaskType} task items:`, error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleStudentClick = (studentId: string) => {
    navigate(`/studentdetails/${studentId}`);
  };

  const closePanelHandler = () => {
    setIsPanelOpen(false);
    setPanelWidth(600); // Reset to default width when closing
    setTimeout(() => setSelectedTaskType(''), 300); // Clear type after animation
  };

  // Handle mouse down on resize handle
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  // Handle mouse move for resizing
  React.useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const newWidth = window.innerWidth - e.clientX;
      const minWidth = 300; // Minimum panel width
      const maxWidth = window.innerWidth * 0.8; // Maximum 80% of screen width

      setPanelWidth(Math.max(minWidth, Math.min(maxWidth, newWidth)));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'default';
      document.body.style.userSelect = 'auto';
    };
  }, [isResizing]);

  const getFilteredTasks = () => {
    // Items are already filtered by type when fetched
    return taskItems;
  };

  const getPanelTitle = () => {
    switch (selectedTaskType) {
      case 'today':
        return 'Due Today Tasks';
      case 'upcoming':
        return 'Upcoming Tasks';
      case 'overdue':
        return 'Overdue Tasks';
      default:
        return '';
    }
  };

  const handleMouseEvent = (state: string, event: any) => {
    if (!event || !event.payload) return;

    const index = data.findIndex(item => item.name === event.payload.name);

    if (state === 'enter') {
      setActiveIndex(index);
    } else if (state === 'leave') {
      setActiveIndex(null);
      setTooltipContent(null);
      return;
    }

    const percentage = calculatePercentage(event.payload.value);
    setTooltipContent({
      x: event.chartX,
      y: event.chartY,
      content: `${event.payload.name}: ${event.payload.value} (${percentage}%)`
    });
  };

  const handlePieClick = (data: any, index: number) => {
    let type = '';
    switch (data.name) {
      case 'Due Today':
        type = 'today';
        break;
      case 'Upcoming':
        type = 'upcoming';
        break;
      case 'Overdue':
        type = 'overdue';
        break;
    }
    if (type) {
      handleTaskClick(type);
    }
  };

  return (
    <div className="bg-white relative">
      <div className="relative group">
        <h2
          className="text-xl text-black font-medium text-center"
          style={{
            fontFamily: "Montserrat",
            lineHeight: "29.26px",
          }}
        >Total Task Count</h2>

        {/* Tooltip */}
        {showTooltip && (
          <div className="absolute left-1/2 transform -translate-x-1/2 top-full mt-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none whitespace-nowrap z-10">
            This data includes all time periods and is not limited to the selected date range.
          </div>
        )}
      </div>

      <div className="flex justify-center relative mt-6 mb-10">
        <PieChart width={200} height={200}>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={0}
            outerRadius={80}
            dataKey="value"
            onMouseEnter={(e) => handleMouseEvent('enter', e)}
            onMouseLeave={(e) => handleMouseEvent('leave', e)}
            onMouseMove={(e) => handleMouseEvent('move', e)}
            onClick={handlePieClick}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[index]}
                style={{
                  cursor: 'pointer',
                  transform: activeIndex === index ? 'scale(1.1)' : 'scale(1)',
                  transformOrigin: '50% 50%',
                  transition: 'transform 0.3s ease-in-out',
                  outline: 'none'
                }}
                className="focus:outline-none"
              />
            ))}
          </Pie>
        </PieChart>
        {tooltipContent && (
          <div
            style={{
              position: 'fixed',
              left: tooltipContent.x + 10,
              top: tooltipContent.y - 20,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              color: 'white',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              pointerEvents: 'none',
              zIndex: 1000,
            }}
          >
            {tooltipContent.content}
          </div>
        )}
      </div>

      <div className="flex items-center justify-center gap-2 mt-4 text-xs flex-wrap mb-14">
        <div className="flex items-center">
          <div className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: '#3483be' }}></div>
          Due Today
        </div>
        <div className="flex items-center">
          <div className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: '#02385e' }}></div>
          Upcoming
        </div>
        <div className="flex items-center">
          <div className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: '#fc583d' }}></div>
          Overdue
        </div>
      </div>

      {/* Total Summary */}
      <div className="mt-4 pt-3 border-t border-gray-200">
        <div className="flex justify-center">
          <span className="text-lg font-semibold text-gray-800">Total: {taskMetrics?.totalTasks} tasks</span>
        </div>
      </div>

      {/* Overlay */}
      {isPanelOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
          onClick={closePanelHandler}
        />
      )}

      {/* Right Side Panel */}
      <div
        className={`fixed top-0 right-0 h-full bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${isPanelOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        style={{ width: `${panelWidth}px` }}
      >
        {/* Resize Handle */}
        <div
          className="absolute left-0 top-0 w-1 h-full bg-gray-300 hover:bg-blue-500 cursor-ew-resize z-10 transition-colors duration-200"
          onMouseDown={handleMouseDown}
          title="Drag to resize"
        />

        <div className="p-6 h-full flex flex-col">
          {/* Panel Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-800" style={{ fontFamily: "Montserrat" }}>
                {getPanelTitle()}
              </h2>
              {!loading && taskItems.length > 0 && (
                <p className="text-sm text-gray-600 mt-1">
                  Showing {taskItems.length} {selectedTaskType} task{taskItems.length !== 1 ? 's' : ''}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={closePanelHandler}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Panel Content */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-600">Loading tasks...</span>
              </div>
            ) : getFilteredTasks().length > 0 ? (
              <div className={panelWidth > 600 ? "grid grid-cols-2 gap-4" : "space-y-4"}>
                {getFilteredTasks().map((item, index) => (
                  <div
                    key={item.id}
                    className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:shadow-md transition-shadow duration-200"
                    style={{
                      animation: `slideIn 0.3s ease-out ${index * 0.1}s both`
                    }}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold text-gray-800 line-clamp-2">{item.task}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ml-2 flex-shrink-0 ${item.status === 'upcoming' ? 'bg-green-100 text-green-800' :
                        item.status === 'today' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                        {item.status === 'today' ? 'Due Today' :
                          item.status === 'upcoming' ? 'Upcoming' : 'Overdue'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">👤 Lead: {item.leadName}</p>
                    <p className="text-sm text-gray-600 mb-2">📞 {item.leadNumber}</p>
                    <p className="text-sm text-gray-600 mb-2">📅 Due: {format(item.dueDate.toDate(), 'PPP p')}</p>
                    <p className="text-sm text-gray-600 mb-2">👨‍💼 Assigned to: {item.assigneeName}</p>
                    {item.assignerName && (
                      <p className="text-sm text-gray-600 mb-2">📋 Assigned by: {item.assignerName}</p>
                    )}
                    <div className="mt-4 flex justify-end">
                      <button
                        onClick={() => handleStudentClick(item.leadId)}
                        className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200 border border-blue-600 rounded-md py-1 px-2"
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-lg font-medium">No tasks found</p>
                <p className="text-sm mt-1">There are no {selectedTaskType} tasks at the moment.</p>
              </div>
            )}

            {/* Show More Button */}
            {!loading && hasMore && getFilteredTasks().length > 0 && (
              <div className="mt-4 text-center">
                <button
                  onClick={handleShowMore}
                  disabled={loadingMore}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {loadingMore ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Loading more...
                    </div>
                  ) : (
                    'Show More'
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TotalTaskCount;